import { useState } from 'react'

/** The picker's own state: the chips chosen and the words typed beside them. */
export interface ChoiceState {
  selected: string[]
  freeText: string
}

interface IntuitiveChoiceProps {
  /**
   * Opt in to a picker that survives being taken off screen. The caller keeps
   * the state (`onStateChange` hands it over on every change) and passes it
   * back here as `initialState` when the picker is shown again. Callers that
   * pass neither get the picker as it always was: it starts empty.
   */
  initialState?: ChoiceState
  onStateChange?: (state: ChoiceState) => void
  prompt: string
  options: string[]
  onComplete: (value: string) => void
  allowMultiple?: boolean
  placeholder?: string
  ctaLabel?: string
  skipLabel?: string
  isLoadingOptions?: boolean
}

export default function IntuitiveChoice({
  initialState,
  onStateChange,
  prompt,
  options,
  onComplete,
  allowMultiple = true,
  placeholder = 'Or write your own...',
  ctaLabel = 'Continue',
  skipLabel,
  isLoadingOptions = false,
}: IntuitiveChoiceProps) {
  const [selected, setSelected] = useState<string[]>(initialState?.selected ?? [])
  const [freeText, setFreeText] = useState(initialState?.freeText ?? '')

  function toggle(opt: string) {
    const next = allowMultiple
      ? (selected.includes(opt) ? selected.filter(o => o !== opt) : [...selected, opt])
      : (selected.includes(opt) ? [] : [opt])
    setSelected(next)
    onStateChange?.({ selected: next, freeText })
  }

  function changeText(text: string) {
    setFreeText(text)
    onStateChange?.({ selected, freeText: text })
  }

  function handleComplete() {
    const parts = [...selected]
    if (freeText.trim()) parts.push(freeText.trim())
    onComplete(parts.join(', '))
  }

  return (
    <div className="flex-1 flex flex-col message-appear">
      <p className="font-display text-xl leading-snug mb-8"
        style={{ color: '#1C1128', fontStyle: 'italic', fontWeight: 400 }}>
        {prompt}
      </p>

      {isLoadingOptions ? (
        <div className="flex flex-wrap gap-2 mb-6">
          {[90, 115, 80, 130, 100].map((w, i) => (
            <div
              key={i}
              style={{
                width: w,
                height: 36,
                borderRadius: 9999,
                background: '#F4EFF8',
                border: '1.5px solid #E4D5EE',
                animation: `pulse 1.4s ease-in-out ${i * 0.12}s infinite`,
              }}
            />
          ))}
        </div>
      ) : (
        <div className="flex flex-wrap gap-2 mb-6">
          {options.map(opt => {
            const isSelected = selected.includes(opt)
            return (
              <button
                key={opt}
                onClick={() => toggle(opt)}
                className="px-4 py-2 rounded-full text-sm font-medium transition-all"
                style={{
                  background: isSelected ? '#3D1F62' : 'white',
                  color: isSelected ? 'white' : '#6B5F7A',
                  border: isSelected ? '1.5px solid #3D1F62' : '1.5px solid #C4B5D4',
                  cursor: 'pointer',
                }}
              >
                {opt}
              </button>
            )
          })}
        </div>
      )}

      <textarea
        value={freeText}
        onChange={e => changeText(e.target.value)}
        placeholder={placeholder}
        rows={3}
        className="w-full px-4 py-3 rounded-xl border text-sm leading-relaxed resize-none focus:outline-none"
        style={{ borderColor: '#C4B5D4', background: 'white', color: '#1C1128' }}
        onFocus={e => { e.target.style.borderColor = '#9B72C0'; e.target.style.boxShadow = '0 0 0 3px rgba(155,114,192,0.12)' }}
        onBlur={e => { e.target.style.borderColor = '#C4B5D4'; e.target.style.boxShadow = 'none' }}
      />

      <div className="flex-1" />

      <div className="flex flex-col gap-3 mt-6">
        <button
          onClick={handleComplete}
          className="w-full py-4 rounded-xl font-medium tracking-wide"
          style={{ background: '#3D1F62', color: 'white', border: 'none', cursor: 'pointer' }}
        >
          {ctaLabel}
        </button>
        {skipLabel && (
          <button
            onClick={() => {
              // Skipping is "no answer", and the screen must come back that way:
              // otherwise Back would show chips as chosen that were not handed
              // on, and Continue would hand them on as a change.
              onStateChange?.({ selected: [], freeText: '' })
              onComplete('')
            }}
            className="w-full py-3 rounded-xl text-sm"
            style={{ background: 'transparent', border: 'none', color: '#C4B5D4', cursor: 'pointer' }}
          >
            {skipLabel}
          </button>
        )}
      </div>
    </div>
  )
}
